import { Box, HStack, Text, VStack } from '@chakra-ui/react';

import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XmarkCircle } from 'iconoir-react';

interface PasswordRequirementsProps {
  password: string;
  passwordConfirmation?: string;
  showMatchRequirement?: boolean;
}

interface RequirementProps {
  met: boolean;
  label: string;
}

const Requirement: React.FC<RequirementProps> = ({ met, label }) => {
  return (
    <HStack gap={2} alignItems="center">
      <motion.div
        animate={{
          scale: met ? 1.05 : 1,
        }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {met ? (
          <motion.div
            initial={{ scale: 0.7, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 0.4,
              ease: [0.175, 0.885, 0.32, 1.275],
              scale: {
                type: 'spring',
                stiffness: 300,
                damping: 15,
              },
            }}
          >
            <Box as="span" display="inline-flex" color="green.600">
              <CheckCircle width={16} height={16} />
            </Box>
          </motion.div>
        ) : (
          <Box as="span" display="inline-flex" color="gray.400">
            <XmarkCircle width={16} height={16} />
          </Box>
        )}
      </motion.div>
      <motion.div
        animate={{
          color: met ? 'var(--chakra-colors-green-fg)' : 'var(--chakra-colors-gray-400)',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
      >
        <Text
          fontSize="sm"
          lineHeight="short"
          style={{
            fontWeight: met ? 500 : 400,
          }}
        >
          {label}
        </Text>
      </motion.div>
    </HStack>
  );
};

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  passwordConfirmation,
  showMatchRequirement = false,
}) => {
  const { t } = useTranslation();

  const requirements = [
    {
      met: password.length >= 8,
      label: t('auth.passwordRequirements.minLength'),
    },
    {
      met: /[A-Za-z]/.test(password),
      label: t('auth.passwordRequirements.hasLetter'),
    },
    {
      met: /\d/.test(password),
      label: t('auth.passwordRequirements.hasNumber'),
    },
  ];

  if (showMatchRequirement && passwordConfirmation !== undefined) {
    requirements.push({
      met: password.length > 0 && password === passwordConfirmation,
      label: t('auth.passwordRequirements.passwordsMatch'),
    });
  }

  return (
    <VStack gap={2} alignItems="flex-start" width="full" mt={2}>
      <Text fontSize="sm" fontWeight="medium" color="gray.600">
        {t('auth.passwordRequirements.title')}
      </Text>
      {requirements.map((req, index) => (
        <Requirement key={index} met={req.met} label={req.label} />
      ))}
    </VStack>
  );
};
